import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties, ComponentPropsWithoutRef } from "react";
import { CANVAS_SIZE_ID } from "@circle-react-shared/BuilderUI/Canvas/constants";
import { IFRAME_ID } from "@circle-react-shared/BuilderUI/Frames/constants";
import { useBuilderStore } from "@circle-react-shared/BuilderUI/Hooks/useBuilderStore";
import { useIFrameDocument } from "@circle-react-shared/BuilderUI/Hooks/useIFrameDocument";

export interface CanvasToolsProps extends ComponentPropsWithoutRef<"div"> {}

export const CanvasTools = ({ children }: CanvasToolsProps) => {
  const value = useBuilderStore(state => state.getSelectedBlock());
  const selectedBlock = value?.block;

  const selectedBlockPath =
    useBuilderStore(state => state.selectedBlockPath) ?? "";
  const iFrameDocument = useIFrameDocument(IFRAME_ID, selectedBlockPath);
  const canvasSizeElement = document.getElementById(CANVAS_SIZE_ID);

  const [styles, setStyles] = useState<CSSProperties>({});

  const updatePosition = useCallback(() => {
    const { id } = selectedBlock || {};
    if (!id || !iFrameDocument) {
      return;
    }

    const blockElement = iFrameDocument.getElementById(id);
    if (!blockElement) {
      return;
    }

    const rect = blockElement.getBoundingClientRect();
    setStyles({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
    });
  }, [selectedBlock, iFrameDocument]);

  const resizeObserver = useMemo(
    () =>
      new ResizeObserver(entries => {
        for (const entry of entries) {
          if (entry.contentRect) {
            updatePosition();
          }
        }
      }),
    [updatePosition],
  );

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    if (!iFrameDocument || !canvasSizeElement) {
      return;
    }
    iFrameDocument.addEventListener("scroll", updatePosition);
    resizeObserver.observe(canvasSizeElement);

    return () => {
      iFrameDocument.removeEventListener("scroll", updatePosition);
      resizeObserver.disconnect();
    };
  }, [iFrameDocument, canvasSizeElement, resizeObserver, updatePosition]);

  useEffect(() => {
    const { id } = selectedBlock || {};
    if (!id || !iFrameDocument) {
      return;
    }

    const blockElement = iFrameDocument.getElementById(id);
    if (!blockElement) {
      return;
    }

    resizeObserver.observe(blockElement);

    const images = blockElement.querySelectorAll("img");
    images.forEach(img => {
      img.addEventListener("load", updatePosition);
    });

    return () => {
      images.forEach(img => {
        img.removeEventListener("load", updatePosition);
      });
    };
  }, [iFrameDocument, selectedBlock, resizeObserver, updatePosition]);

  if (!selectedBlock) {
    return null;
  }

  return (
    <div
      className="border-circle pointer-events-none absolute z-10 border-4"
      data-testid="builder-ui-canvas-tools"
      style={styles}
    >
      {children}
    </div>
  );
};
